/**
 * Reading the expiry out of our own access token, on the SERVER's clock.
 *
 * The browser never verifies the token — the server does that on every call.
 * All the client needs is "when does this sign-in run out", so it can send the
 * user to the login page the moment it does instead of letting the next click
 * fail. That answer is only as good as the clock it is measured against, and
 * a PC clock on the wrong timezone (IST set as UTC is the classic) is 5.5 h
 * out — enough to end a 10-hour session after 4.5 h.
 *
 * So the offset is learned once per sign-in from the token's own `iat`: the
 * server stamped it a moment ago, so `Date.now() - iat` is how far this
 * browser's clock is from the server's. Every expiry check subtracts it.
 */

const SKEW_KEY = 'ukn_clock_skew_ms';

type Claims = { exp?: number; iat?: number };

export function jwtClaims(token: string): Claims | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    return claims && typeof claims === 'object' ? (claims as Claims) : null;
  } catch {
    return null;
  }
}

/** Learn this browser's clock offset from a token the server has JUST minted. */
export function learnClockSkew(freshToken: string): void {
  const iat = jwtClaims(freshToken)?.iat;
  if (typeof iat !== 'number') return;
  try { localStorage.setItem(SKEW_KEY, String(Date.now() - iat * 1000)); } catch { /* ignore */ }
}

function clockSkewMs(): number {
  try {
    const v = Number(localStorage.getItem(SKEW_KEY));
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}

/** Milliseconds until the token expires by the server's clock; null when it
 *  carries no `exp` (then only the server can say, and it will — with a 401). */
export function msUntilExpiry(token: string): number | null {
  const exp = jwtClaims(token)?.exp;
  if (typeof exp !== 'number') return null;
  return exp * 1000 - (Date.now() - clockSkewMs());
}

export function isTokenExpired(token: string): boolean {
  const ms = msUntilExpiry(token);
  return ms !== null && ms <= 0;
}
